import {
  emptyRfqLineInput,
  emptyRfqTargetInput,
  rfqTargetQuantityAsNumber,
  type MrpRfq,
  type MrpRfqLineInput,
  type MrpRfqTargetInput,
} from "@/lib/rfqApi";

export const RFQ_COMPOSE_SESSION_DRAFT_KEY = "mrp-rfq-compose-draft:v1";

export interface RfqComposeSessionDraftV1 {
  v: 1;
  savedAtIso: string;
  supplierId: string;
  supplierLabel: string | null;
  supplierSearch: string;
  internalNotes: string;
  externalNotes: string;
  neededByDate: string;
  currency: string;
  lines: MrpRfqLineInput[];
  stagedExternalDocumentsByLineIndex?: Record<string, string[]>;
  stagedInternalDocumentsByLineIndex?: Record<string, string[]>;
}

export interface RfqComposeSessionBootstrap {
  supplierId: string;
  supplierLabel: string | null;
  supplierSearch: string;
  internalNotes: string;
  externalNotes: string;
  neededByDate: string;
  currency: string;
  lines: MrpRfqLineInput[];
  stagedExternalDocumentsByLineIndex: Record<number, string[]>;
  stagedInternalDocumentsByLineIndex: Record<number, string[]>;
}

const DEFAULT_CURRENCY = "USD";

export function normalizeComposeStagedExternalDocumentsByLineIndex(
  raw: unknown,
): Record<number, string[]> {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }
  const normalized: Record<number, string[]> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const lineIndex = Number.parseInt(key, 10);
    if (!Number.isFinite(lineIndex) || lineIndex < 0) continue;
    if (!Array.isArray(value)) continue;
    const ids = value
      .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
      .filter((entry) => entry !== "");
    if (ids.length === 0) continue;
    normalized[lineIndex] = [...new Set(ids)];
  }
  return normalized;
}

export function serializeComposeStagedExternalDocumentsByLineIndex(
  staged: Record<number, string[]>,
): Record<string, string[]> | undefined {
  const serialized: Record<string, string[]> = {};
  for (const [lineIndexRaw, ids] of Object.entries(staged)) {
    const lineIndex = Number.parseInt(lineIndexRaw, 10);
    if (!Number.isFinite(lineIndex) || lineIndex < 0 || ids.length === 0) continue;
    serialized[String(lineIndex)] = [...new Set(ids.map((id) => id.trim()).filter((id) => id !== ""))];
  }
  return Object.keys(serialized).length > 0 ? serialized : undefined;
}

export function composeStagedExternalDocumentsIsEmpty(staged: Record<number, string[]>): boolean {
  return Object.values(staged).every((ids) => ids.length === 0);
}

export function normalizeComposeStagedInternalDocumentsByLineIndex(
  raw: unknown,
): Record<number, string[]> {
  return normalizeComposeStagedExternalDocumentsByLineIndex(raw);
}

export function serializeComposeStagedInternalDocumentsByLineIndex(
  staged: Record<number, string[]>,
): Record<string, string[]> | undefined {
  return serializeComposeStagedExternalDocumentsByLineIndex(staged);
}

export function composeStagedInternalDocumentsIsEmpty(staged: Record<number, string[]>): boolean {
  return composeStagedExternalDocumentsIsEmpty(staged);
}

export function composeStagedDocumentsIsEmpty(
  external: Record<number, string[]>,
  internal: Record<number, string[]>,
): boolean {
  return composeStagedExternalDocumentsIsEmpty(external) && composeStagedInternalDocumentsIsEmpty(internal);
}

function coerceTargetInput(raw: unknown): MrpRfqTargetInput | null {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const target = raw as Record<string, unknown>;
  const quantity =
    typeof target.quantity === "number" && Number.isFinite(target.quantity)
      ? target.quantity
      : Number.parseInt(String(target.quantity ?? "").replace(/,/g, ""), 10);
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  const priceRaw = target.target_unit_price;
  let target_unit_price: number | null = null;
  if (priceRaw != null && priceRaw !== "") {
    const parsed = typeof priceRaw === "number" ? priceRaw : Number.parseFloat(String(priceRaw));
    target_unit_price = Number.isFinite(parsed) ? parsed : null;
  }
  return { quantity, target_unit_price };
}

function coerceLineInput(raw: unknown): MrpRfqLineInput | null {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const line = raw as Record<string, unknown>;
  const targetsRaw = Array.isArray(line.targets) ? line.targets : [];
  const targets = targetsRaw
    .map((entry) => coerceTargetInput(entry))
    .filter((entry): entry is MrpRfqTargetInput => entry != null);
  return {
    sku: typeof line.sku === "string" ? line.sku : "",
    description: typeof line.description === "string" ? line.description : "",
    internal_notes:
      typeof line.internal_notes === "string"
        ? line.internal_notes
        : typeof line.notes === "string"
          ? line.notes
          : "",
    external_notes: typeof line.external_notes === "string" ? line.external_notes : "",
    targets: targets.length > 0 ? targets : [emptyRfqTargetInput()],
  };
}

/** Drop null or malformed entries; always returns at least one empty line. */
export function normalizeRfqComposeLines(raw: unknown): MrpRfqLineInput[] {
  if (!Array.isArray(raw)) return [emptyRfqLineInput()];
  const lines = raw
    .map((entry) => coerceLineInput(entry))
    .filter((entry): entry is MrpRfqLineInput => entry != null);
  return lines.length > 0 ? lines : [emptyRfqLineInput()];
}

export function rfqLineInputsFromRfq(rfq: Pick<MrpRfq, "lines">): MrpRfqLineInput[] {
  return rfq.lines.length > 0
    ? rfq.lines.map((line) => ({
        sku: line.sku,
        description: line.description,
        internal_notes: line.internal_notes,
        external_notes: line.external_notes,
        targets:
          line.targets.length > 0
            ? line.targets.map((target) => ({
                quantity: String(target.quantity),
                target_unit_price: target.target_unit_price,
              }))
            : [emptyRfqTargetInput()],
      }))
    : [emptyRfqLineInput()];
}

function serializeRfqDraftSnapshot(
  internalNotes: string,
  externalNotes: string,
  neededByDate: string,
  currency: string,
  lines: MrpRfqLineInput[],
): string {
  return JSON.stringify({
    header: {
      internal_notes: internalNotes.trim() || null,
      external_notes: externalNotes.trim() || null,
      needed_by_date: neededByDate.trim() || null,
      currency: currency.trim().toUpperCase(),
    },
    lines: normalizeRfqComposeLines(lines)
      .filter((line) => line.sku.trim() !== "")
      .map((line) => ({
        sku: line.sku.trim(),
        description: line.description,
        internal_notes: line.internal_notes,
        external_notes: line.external_notes,
        targets: line.targets.map((target) => ({
          quantity: target.quantity,
          target_unit_price: target.target_unit_price,
        })),
      })),
  });
}

export function rfqDraftSnapshotFromRfq(rfq: MrpRfq): string {
  return serializeRfqDraftSnapshot(
    rfq.internal_notes ?? "",
    rfq.external_notes ?? "",
    rfq.needed_by_date ?? "",
    rfq.currency,
    rfqLineInputsFromRfq(rfq),
  );
}

export function rfqDraftSnapshotFromForm(
  internalNotes: string,
  externalNotes: string,
  neededByDate: string,
  currency: string,
  lines: MrpRfqLineInput[],
): string {
  return serializeRfqDraftSnapshot(internalNotes, externalNotes, neededByDate, currency, lines);
}

type RfqDraftSnapshotPayload = {
  header: {
    internal_notes: string | null;
    external_notes: string | null;
    needed_by_date: string | null;
    currency: string;
  };
  lines: Array<{
    sku: string;
    description: string;
    internal_notes: string;
    external_notes: string;
    targets: Array<{ quantity: number; target_unit_price: number | null }>;
  }>;
};

function parseRfqDraftSnapshot(snapshot: string): RfqDraftSnapshotPayload {
  return JSON.parse(snapshot) as RfqDraftSnapshotPayload;
}

function lineTargetsEqual(
  left: RfqDraftSnapshotPayload["lines"][number]["targets"],
  right: RfqDraftSnapshotPayload["lines"][number]["targets"],
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

/** Human-readable list of unsaved RFQ draft fields for Save hover hints. */
export function rfqDraftDirtyFieldLabels(
  savedSnapshot: string | null,
  internalNotes: string,
  externalNotes: string,
  neededByDate: string,
  currency: string,
  lines: MrpRfqLineInput[],
): string | null {
  if (savedSnapshot == null) {
    return null;
  }
  const current = parseRfqDraftSnapshot(
    rfqDraftSnapshotFromForm(internalNotes, externalNotes, neededByDate, currency, lines),
  );
  const saved = parseRfqDraftSnapshot(savedSnapshot);
  const labels: string[] = [];

  if (current.header.internal_notes !== saved.header.internal_notes) {
    labels.push("Internal notes");
  }
  if (current.header.external_notes !== saved.header.external_notes) {
    labels.push("External notes");
  }
  if (current.header.needed_by_date !== saved.header.needed_by_date) {
    labels.push("Needed by date");
  }
  if (current.header.currency !== saved.header.currency) {
    labels.push("Currency");
  }

  const currentLines = current.lines;
  const savedLines = saved.lines;
  const maxLines = Math.max(currentLines.length, savedLines.length);
  for (let index = 0; index < maxLines; index++) {
    const lineNumber = index + 1;
    const currentLine = currentLines[index];
    const savedLine = savedLines[index];
    if (currentLine == null && savedLine != null) {
      labels.push(`Line ${lineNumber} removed`);
      continue;
    }
    if (currentLine != null && savedLine == null) {
      labels.push(`Line ${lineNumber} added`);
      continue;
    }
    if (currentLine == null || savedLine == null) {
      continue;
    }
    if (currentLine.sku !== savedLine.sku) {
      labels.push(`Line ${lineNumber} part number`);
    }
    if (currentLine.description !== savedLine.description) {
      labels.push(`Line ${lineNumber} description`);
    }
    if (currentLine.internal_notes !== savedLine.internal_notes) {
      labels.push(`Line ${lineNumber} internal note`);
    }
    if (currentLine.external_notes !== savedLine.external_notes) {
      labels.push(`Line ${lineNumber} external note`);
    }
    if (!lineTargetsEqual(currentLine.targets, savedLine.targets)) {
      labels.push(`Line ${lineNumber} target quantities`);
    }
  }

  return labels.length > 0 ? labels.join("\n") : null;
}

function coerceLines(raw: unknown): MrpRfqLineInput[] | null {
  const normalized = normalizeRfqComposeLines(raw);
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const hadValidLine = raw.some((entry) => coerceLineInput(entry) != null);
  return hadValidLine ? normalized : null;
}

function defaultBootstrap(): RfqComposeSessionBootstrap {
  return {
    supplierId: "",
    supplierLabel: null,
    supplierSearch: "",
    internalNotes: "",
    externalNotes: "",
    neededByDate: "",
    currency: DEFAULT_CURRENCY,
    lines: [emptyRfqLineInput()],
    stagedExternalDocumentsByLineIndex: {},
    stagedInternalDocumentsByLineIndex: {},
  };
}

function lineIsEmpty(line: MrpRfqLineInput | null | undefined): boolean {
  if (line == null || typeof line !== "object") return true;
  if (typeof line.sku !== "string" || line.sku.trim() !== "") return false;
  if ((line.description ?? "").trim() !== "") return false;
  if ((line.internal_notes ?? "").trim() !== "") return false;
  if ((line.external_notes ?? "").trim() !== "") return false;
  if (!Array.isArray(line.targets) || line.targets.length !== 1) return false;
  const loneTarget = line.targets[0];
  if (!loneTarget) return true;
  if (rfqTargetQuantityAsNumber(loneTarget.quantity) !== 1) return false;
  if (loneTarget.target_unit_price != null) return false;
  return true;
}

export function rfqComposeDraftIsEmpty(
  snapshot: Pick<
    RfqComposeSessionDraftV1,
    | "supplierId"
    | "supplierSearch"
    | "internalNotes"
    | "externalNotes"
    | "neededByDate"
    | "currency"
    | "lines"
    | "stagedExternalDocumentsByLineIndex"
    | "stagedInternalDocumentsByLineIndex"
  >,
): boolean {
  if (snapshot.supplierId.trim() !== "") return false;
  if (snapshot.supplierSearch.trim() !== "") return false;
  if (snapshot.internalNotes.trim() !== "") return false;
  if (snapshot.externalNotes.trim() !== "") return false;
  if (snapshot.neededByDate.trim() !== "") return false;
  if (snapshot.currency.trim() !== "" && snapshot.currency !== DEFAULT_CURRENCY) return false;
  const stagedExternal = normalizeComposeStagedExternalDocumentsByLineIndex(
    snapshot.stagedExternalDocumentsByLineIndex,
  );
  const stagedInternal = normalizeComposeStagedInternalDocumentsByLineIndex(
    snapshot.stagedInternalDocumentsByLineIndex,
  );
  if (!composeStagedDocumentsIsEmpty(stagedExternal, stagedInternal)) return false;
  const normalizedLines = normalizeRfqComposeLines(snapshot.lines);
  if (normalizedLines.length !== 1) return false;
  const loneLine = normalizedLines[0];
  if (!lineIsEmpty(loneLine)) return false;
  return true;
}

export function readRfqComposeSessionDraft(): RfqComposeSessionDraftV1 | null {
  try {
    const raw = sessionStorage.getItem(RFQ_COMPOSE_SESSION_DRAFT_KEY);
    if (raw == null || raw.trim() === "") return null;
    const parsedUnknown: unknown = JSON.parse(raw);
    if (!parsedUnknown || typeof parsedUnknown !== "object") return null;
    const parsed = parsedUnknown as Record<string, unknown>;
    if (parsed.v !== 1) return null;
    const lines = coerceLines(parsed.lines);
    if (!lines) return null;
    const legacyNotes = typeof parsed.notes === "string" ? parsed.notes : "";
    return {
      v: 1,
      savedAtIso: typeof parsed.savedAtIso === "string" ? parsed.savedAtIso : new Date().toISOString(),
      supplierId: typeof parsed.supplierId === "string" ? parsed.supplierId : "",
      supplierLabel: typeof parsed.supplierLabel === "string" ? parsed.supplierLabel : null,
      supplierSearch: typeof parsed.supplierSearch === "string" ? parsed.supplierSearch : "",
      internalNotes:
        typeof parsed.internalNotes === "string"
          ? parsed.internalNotes
          : legacyNotes,
      externalNotes: typeof parsed.externalNotes === "string" ? parsed.externalNotes : "",
      neededByDate: typeof parsed.neededByDate === "string" ? parsed.neededByDate : "",
      currency:
        typeof parsed.currency === "string" && parsed.currency.trim() !== ""
          ? parsed.currency
          : DEFAULT_CURRENCY,
      lines,
      stagedExternalDocumentsByLineIndex: serializeComposeStagedExternalDocumentsByLineIndex(
        normalizeComposeStagedExternalDocumentsByLineIndex(parsed.stagedExternalDocumentsByLineIndex),
      ),
      stagedInternalDocumentsByLineIndex: serializeComposeStagedInternalDocumentsByLineIndex(
        normalizeComposeStagedInternalDocumentsByLineIndex(parsed.stagedInternalDocumentsByLineIndex),
      ),
    };
  } catch {
    return null;
  }
}

export function loadRfqComposeSessionBootstrap(): RfqComposeSessionBootstrap {
  const stored = readRfqComposeSessionDraft();
  if (!stored) return defaultBootstrap();
  return {
    supplierId: stored.supplierId,
    supplierLabel: stored.supplierLabel,
    supplierSearch: stored.supplierSearch,
    internalNotes: stored.internalNotes,
    externalNotes: stored.externalNotes,
    neededByDate: stored.neededByDate,
    currency: stored.currency,
    lines: normalizeRfqComposeLines(stored.lines).map((line) => ({
      ...line,
      targets: line.targets.map((target) => ({ ...target })),
    })),
    stagedExternalDocumentsByLineIndex: normalizeComposeStagedExternalDocumentsByLineIndex(
      stored.stagedExternalDocumentsByLineIndex,
    ),
    stagedInternalDocumentsByLineIndex: normalizeComposeStagedInternalDocumentsByLineIndex(
      stored.stagedInternalDocumentsByLineIndex,
    ),
  };
}

export function persistRfqComposeSessionDraft(snapshot: RfqComposeSessionDraftV1): void {
  try {
    const normalizedSnapshot: RfqComposeSessionDraftV1 = {
      ...snapshot,
      lines: normalizeRfqComposeLines(snapshot.lines).map((line) => ({
        ...line,
        targets: line.targets.map((target) => ({ ...target })),
      })),
      stagedExternalDocumentsByLineIndex: serializeComposeStagedExternalDocumentsByLineIndex(
        normalizeComposeStagedExternalDocumentsByLineIndex(snapshot.stagedExternalDocumentsByLineIndex),
      ),
      stagedInternalDocumentsByLineIndex: serializeComposeStagedInternalDocumentsByLineIndex(
        normalizeComposeStagedInternalDocumentsByLineIndex(snapshot.stagedInternalDocumentsByLineIndex),
      ),
    };
    if (rfqComposeDraftIsEmpty(normalizedSnapshot)) {
      sessionStorage.removeItem(RFQ_COMPOSE_SESSION_DRAFT_KEY);
      return;
    }
    sessionStorage.setItem(RFQ_COMPOSE_SESSION_DRAFT_KEY, JSON.stringify(normalizedSnapshot));
  } catch {
    // sessionStorage may be unavailable or full
  }
}

export function clearRfqComposeSessionDraft(): void {
  try {
    sessionStorage.removeItem(RFQ_COMPOSE_SESSION_DRAFT_KEY);
  } catch {
    // sessionStorage may be unavailable
  }
}
